import { Injectable, inject } from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import {
    IChecklistSchedule,
    IChecklistTimeIntervalGroup,
} from '@fi-shared/checklist/checklist-v2';
import { FiChecklist2ScheduleFactoryService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-factory.service';
import { FiChecklist2ScheduleAddDialogComponent } from '@fi-shared/checklist/schedule/schedule-edit/add-dialog/add-dialog.component';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiChecklist2ScheduleEditTimeIntervalsDialogComponent } from '../schedule-edit/schedule-time/time-intervals-dialog/time-intervals-dialog.component';
import { IChecklist2ScheduleEditTimeIntervalDialogData } from '../schedule-edit/schedule-time/time-intervals-dialog/time-intervals-dialog.component';
import { IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import { FiChecklist2ScheduleIntervalService } from './fi-checklist2-schedule-interval.service';
import { FiDateTimePickerDialogComponent } from '@fi-app/shared/fi-date-time-picker-dialog/fi-date-time-picker-dialog/fi-date-time-picker-dialog.component';

@Injectable()
export class FiChecklist2ScheduleDialogService {
    private fiDialogsService = inject(FiDialogsService);
    private appTextService = inject(AppTextService);
    private fiStateService = inject(FiStateService);
    private saveChangesWarningService = inject(SaveChangesWarningService);
    private fiChecklist2ScheduleFactoryService = inject(FiChecklist2ScheduleFactoryService);
    private fiChecklist2ScheduleIntervalService = inject(FiChecklist2ScheduleIntervalService);

    openAddChecklistScheduleDialogWithChecklistId(checklistId: number, isAuditMode: boolean): void {
        const checklistSchedule = this.fiChecklist2ScheduleFactoryService.getChecklistScheduleInstance(isAuditMode);
        checklistSchedule.ChecklistId = checklistId;
        this.openAddChecklistScheduleDialog(checklistSchedule, isAuditMode);
    }

    openAddChecklistScheduleDialog(checklistSchedule: IChecklistSchedule, isAuditMode: boolean): void {
        this.fiDialogsService
            .custom({
                contentComponentData: {
                    checklistSchedule,
                    auditMode: isAuditMode,
                },
                contentComponentRef: FiChecklist2ScheduleAddDialogComponent,
                hideHeader: true,
                hideDialogTopBorder: true,
                yesButtonText: this.appTextService.get('done'),
                preventUppercaseButtonLabel: true,
                config: {
                    maxWidth: '80%',
                    width: '80%',
                    autoFocus: false,
                    closeOnNavigation: false,
                    panelClass: ['fi-rounded-dialog-panel', 'fi-rounded-dialog-full-round-buttons'],
                },
            })
            .onYesButtonClick((newChecklistSchedule: IChecklistSchedule) => {
                this.fiStateService.go(FiStateNames.getChecklistV2RootState(isAuditMode).Schedule.Add, {
                    checklistSchedule: newChecklistSchedule,
                });
            })
            .afterClosed(() => {
                this.saveChangesWarningService.closeDialogOnTransition = false;
            });
        this.saveChangesWarningService.closeDialogOnTransition = true;
    }

    getEditChecklistScheduleTimeDialog(
        checklistSchedule: IChecklistSchedule,
        intervalGroup: IChecklistTimeIntervalGroup,
        auditMode: boolean,
    ): IFiDialogResult<IChecklist2ScheduleEditTimeIntervalDialogData> {
        return this.fiDialogsService.custom({
            contentComponentRef: FiChecklist2ScheduleEditTimeIntervalsDialogComponent,
            contentComponentData: {
                scheduleType: checklistSchedule.ScheduleType,
                timeIntervalType: checklistSchedule.TimeIntervalType,
                timeIntervalGroup: intervalGroup,
                remindTime: checklistSchedule.RemindTime,
                hasAnswers: checklistSchedule.HasAnswers,
                auditMode: auditMode,
            },
            headerText: this.appTextService.get('checklist.time_schedule'),
            yesButtonText: this.appTextService.get('done'),
            hideDialogTopBorder: true,
            preventUppercaseButtonLabel: true,
            config: {
                maxWidth: '90%',
                minWidth: '70%',
                autoFocus: false,
                closeOnNavigation: false,
                panelClass: ['fi-rounded-dialog-panel', 'fi-rounded-dialog-full-round-buttons'],
            },
        });
    }

    openRangesErrorDialog(): void {
        this.fiDialogsService.error({
            headerText: this.appTextService.get('checklist.schedule_range_conflict_dialog_header'),
            messageText: this.appTextService.get('checklist.schedule_range_conflict_dialog_message', {
                '{0}': this.fiChecklist2ScheduleIntervalService.intervalMinGap,
            }),
        });
    }

    getTimePickerDialog(date, minDate): IFiDialogResult<Date> {
        return this.fiDialogsService
            .custom({
                contentComponentRef: FiDateTimePickerDialogComponent,
                contentComponentData: { date, minDate },
                hideDialogTopBorder: true,
                isNoButtonHidden: true,
                isYesIconButton: true,
                isNoButtonPrimaryColor: true,
                yesButtonIconClass: 'BrandedColor fa-sharp fa-regular fa-check',
                config: {
                    autoFocus: false,
                    restoreFocus: false,
                    panelClass: 'fi-date-time-picker-panel',
                },
            })
    }
}
